<template>
  <div class="app-container">
    <!-- Header -->
    <div class="header">
      <div class="header-content">
        <div class="header-icon">
          <svg
            class="header-svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            ></path>
          </svg>
        </div>
        <div>
          <h1 class="header-title">MOH Reports System</h1>
          <p class="header-subtitle">
            Ministry of Health - Electronic Reporting System
          </p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-container">
      <!-- 🏥 OUTPATIENT -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">🏥</span>
          <h2 class="section-title">Outpatient (OPD)</h2>
          <span class="section-count">3</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.outpatient"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 🛏️ INPATIENT -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">🛏️</span>
          <h2 class="section-title">Inpatient (IPD)</h2>
          <span class="section-count">2</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.inpatient"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 🤰 MATERNITY -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">🤰</span>
          <h2 class="section-title">Maternity / MCH</h2>
          <span class="section-count">4</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.maternity"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 👶 CHILD HEALTH -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">👶</span>
          <h2 class="section-title">Child Health / Immunization</h2>
          <span class="section-count">3</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.childHealth"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 🦠 HIV/TB -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">🦠</span>
          <h2 class="section-title">HIV / TB / Special Programs</h2>
          <span class="section-count">6</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.hivTb"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 🧪 LABORATORY -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">🧪</span>
          <h2 class="section-title">Laboratory</h2>
          <span class="section-count">2</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.laboratory"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 💊 PHARMACY -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">💊</span>
          <h2 class="section-title">Pharmacy / Commodities</h2>
          <span class="section-count">3</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.pharmacy"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 🦟 SURVEILLANCE -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">🦟</span>
          <h2 class="section-title">Disease Surveillance</h2>
          <span class="section-count">2</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.surveillance"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>

      <!-- 📊 ADMIN -->
      <div class="section">
        <div class="section-header">
          <span class="section-icon">📊</span>
          <h2 class="section-title">Admin / General</h2>
          <span class="section-count">2</span>
        </div>
        <div class="cards-wrapper">
          <a
            v-for="r in reports.admin"
            :key="r.code"
            :href="r.link"
            class="report-card"
          >
            <div class="card-icon">📄</div>
            <div class="card-code">{{ r.code }}</div>
            <div class="card-title">{{ r.title }}</div>
          </a>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p>
        © {{ currentYear }} Ministry of Health - Electronic Reporting System
      </p>
      <p class="footer-note">Click any card to access the report</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "MOHReports",
  data() {
    return {
      currentYear: new Date().getFullYear(),
      reports: {
        outpatient: [
          {
            code: "MOH 204A",
            title: "Under Five Outpatient Register",
            link: "/reports/moh-204a",
          },
          {
            code: "MOH 204B",
            title: "Over Five Outpatient Register",
            link: "/reports/moh-204b",
          },
          {
            code: "MOH 705",
            title: "Monthly OPD Summary",
            link: "/reports/moh-705",
          },
        ],
        inpatient: [
          {
            code: "MOH 301",
            title: "Inpatient Register",
            link: "/reports/moh-301",
          },
          {
            code: "MOH 711",
            title: "Monthly Inpatient Summary",
            link: "/reports/moh-711",
          },
        ],
        maternity: [
          { code: "MOH 405", title: "ANC Register", link: "/reports/moh-405" },
          {
            code: "MOH 333",
            title: "Maternity Register",
            link: "/reports/moh-333",
          },
          {
            code: "MOH 406",
            title: "Postnatal Register",
            link: "/reports/moh-406",
          },
          {
            code: "MOH 711A",
            title: "Reproductive & Maternal Health Summary",
            link: "/reports/moh-711a",
          },
        ],
        childHealth: [
          {
            code: "MOH 510",
            title: "Immunization Register",
            link: "/reports/moh-510",
          },
          {
            code: "MOH 710",
            title: "Immunization Summary Report",
            link: "/reports/moh-710",
          },
          {
            code: "MOH 511",
            title: "Child Welfare Clinic Register",
            link: "/reports/moh-511",
          },
        ],
        hivTb: [
          {
            code: "MOH 257",
            title: "HTS (HIV Testing Services) Register",
            link: "/reports/moh-257",
          },
          {
            code: "MOH 731",
            title: "HIV Monthly Summary",
            link: "/reports/moh-731",
          },
          {
            code: "MOH 406A",
            title: "PMTCT Register",
            link: "/reports/moh-406a",
          },
          {
            code: "MOH 406B",
            title: "HEI (HIV Exposed Infant) Register",
            link: "/reports/moh-406b",
          },
          { code: "MOH 363A", title: "TB Register", link: "/reports/moh-363a" },
          {
            code: "MOH 711B",
            title: "TB Summary Report",
            link: "/reports/moh-711b",
          },
        ],
        laboratory: [
          {
            code: "MOH 335",
            title: "Laboratory Register",
            link: "/reports/moh-335",
          },
          {
            code: "MOH 706",
            title: "Laboratory Monthly Summary",
            link: "/reports/moh-706",
          },
        ],
        pharmacy: [
          {
            code: "MOH 643",
            title: "Daily Activity Register (Pharmacy)",
            link: "/reports/moh-643",
          },
          {
            code: "MOH 711",
            title: "Commodity Consumption Report",
            link: "/reports/moh-711-commodity",
          },
          {
            code: "MOH 732",
            title: "ARV Dispensing Tool",
            link: "/reports/moh-732",
          },
        ],
        surveillance: [
          {
            code: "MOH 505",
            title: "IDSR Weekly Report",
            link: "/reports/moh-505",
          },
          {
            code: "MOH 505B",
            title: "IDSR Immediate Notification Form",
            link: "/reports/moh-505b",
          },
        ],
        admin: [
          {
            code: "MOH 717",
            title: "Service Delivery Report",
            link: "/reports/moh-717",
          },
          {
            code: "MOH 516",
            title: "Referral Register",
            link: "/reports/moh-516",
          },
        ],
      },
    };
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

/* Header */
.header {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-bottom: 3px solid #10b981;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-content {
  /* max-width: 1200px; */
  margin-top: -2.4em;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  background: #d1fae5;
  padding: 10px;
  border-radius: 12px;
}

.header-svg {
  width: 28px;
  height: 28px;
  color: #059669;
}

.header-title {
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
}

.header-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
}

/* Main */
.main-container {
  /* max-width: 1200px; */
  margin: 0 auto;
  padding: 24px;
}

/* Section */
.section {
  margin-bottom: 32px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.section-icon {
  font-size: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #374151;
}

.section-count {
  background: #e5e7eb;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
}

/* Cards Wrapper - inline flex wrap */
.cards-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

/* Report Card - simple inline block */
.report-card {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 18px;
  min-width: 140px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.report-card:hover {
  border-color: #10b981;
  background: #f0fdf4;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.card-icon {
  font-size: 28px;
  margin-bottom: 8px;
  color: #6b7280;
}

.report-card:hover .card-icon {
  color: #059669;
}

.card-code {
  font-size: 11px;
  font-family: monospace;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 6px;
  color: #4b5563;
  margin-bottom: 6px;
}

.report-card:hover .card-code {
  background: #d1fae5;
  color: #065f46;
}

.card-title {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.3;
}

.report-card:hover .card-title {
  color: #065f46;
}

/* Footer */
.footer {
  background: white;
  border-top: 1px solid #e5e7eb;
  text-align: center;
  padding: 20px;
  margin-top: 16px;
  font-size: 13px;
  color: #6b7280;
}

.footer-note {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

/* Responsive */
@media (max-width: 640px) {
  .main-container {
    padding: 16px;
  }

  .report-card {
    min-width: 120px;
    padding: 10px 12px;
  }

  .card-icon {
    font-size: 22px;
  }

  .card-title {
    font-size: 10px;
  }

  .header-title {
    font-size: 18px;
  }
}
</style>
